import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  Globe2,
  ImagePlus,
  LayoutGrid,
  Link2,
  Loader2,
  MapPin,
  Megaphone,
  Pencil,
  Plus,
  Power,
  Search,
  Send,
  Trash2,
  Truck,
  Type,
  Users,
  Warehouse,
  X,
} from "lucide-react";
import {
  broadcastsService,
  buildBroadcastImageUrl,
} from "../../../services/broadcasts.service";

const BORDER = "#f2c6cc";
const PINK = "#d86a78";
const PINK_HOVER = "#c95b69";
const TITLE_LIMIT = 20;
const BODY_LIMIT = 50;

const audienceOptions = [
  { value: "users", label: "Users", icon: Users },
  { value: "delivery_partners", label: "Delivery Partners", icon: Truck },
  { value: "warehouses", label: "Warehouses", icon: Warehouse },
];

const filterOptions = [
  { value: "all", label: "All" },
  { value: "state", label: "State wise" },
  { value: "city", label: "City wise" },
  { value: "warehouse", label: "Warehouse wise" },
];

const initialForm = {
  audience: "users",
  title: "",
  body: "",
  actionUrl: "",
  filterType: "all",
  state: "",
  city: "",
  warehouseId: "",
  startAtLocal: "",
  endAtLocal: "",
  image: null,
  sendEmail: false,
};


function BroadcastFilterDropdown({
  value,
  onChange,
  options = [],
  minWidth = "min-w-[150px]",
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selected =
    options.find((option) => String(option.value) === String(value)) ||
    options[0] ||
    { value: "", label: "Select" };

  useEffect(() => {
    const handleOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div ref={wrapperRef} className={`relative ${minWidth}`}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-[44px] w-full items-center justify-between rounded-[14px] border bg-white px-4 text-left text-sm font-medium outline-none transition ${
          open
            ? "border-[#d86a78] ring-2 ring-[#f7d4da]"
            : "border-[#e7eaf0] text-slate-700 hover:border-[#d86a78]"
        }`}
      >
        <span className="min-w-0 flex-1 truncate">{selected.label}</span>
        <ChevronDown
          size={16}
          className={`ml-3 shrink-0 text-slate-400 transition ${
            open ? "rotate-180 text-[#d86a78]" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[50px] z-[220] max-h-[250px] overflow-y-auto rounded-[14px] border border-[#f2c6cc] bg-white p-1 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          {options.map((option) => {
            const active = String(option.value) === String(value);

            return (
              <button
                key={String(option.value || "all")}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`block w-full rounded-[10px] px-3 py-2.5 text-left text-sm transition ${
                  active
                    ? "bg-[#fff1f3] font-semibold text-[#d86a78]"
                    : "text-slate-700 hover:bg-[#fff7f8] hover:text-[#d86a78]"
                }`}
              >
                <span className="block truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function Card({ children, className = "" }) {
  return (
    <div className={`rounded-[24px] border bg-white shadow-sm ${className}`} style={{ borderColor: BORDER }}>
      {children}
    </div>
  );
}

function ModalShell({ open, title, subtitle, children, onClose, width = "max-w-[1100px]" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/45 px-4 py-6">
      <div className={`relative w-full ${width} max-h-[92vh] overflow-hidden rounded-[28px] border bg-white shadow-2xl`} style={{ borderColor: BORDER }}>
        <div className="flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6" style={{ borderColor: BORDER }}>
          <div>
            <h3 className="text-[24px] font-bold text-slate-900">{title}</h3>
            {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-[#d86a78]">
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[calc(92vh-84px)] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder = "", type = "text", icon: Icon, maxLength, required = false }) {
  return (
    <label className="block space-y-2.5">
      <span className="text-[13px] font-semibold text-slate-800">{label} {required ? <span className="text-red-500">*</span> : null}</span>
      <div className="relative">
        {Icon ? <Icon size={17} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" /> : null}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          className={`h-[54px] w-full rounded-[16px] border bg-white ${Icon ? "pl-12" : "pl-4"} ${maxLength ? "pr-16" : "pr-4"} text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]`}
          style={{ borderColor: "#dfe5ee" }}
        />
        {maxLength ? <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-medium text-slate-400">{String(value || "").length}/{maxLength}</span> : null}
      </div>
    </label>
  );
}

function TextArea({ label, value, onChange, placeholder = "", maxLength = BODY_LIMIT, required = false }) {
  return (
    <label className="block space-y-2.5">
      <span className="text-[13px] font-semibold text-slate-800">{label} {required ? <span className="text-red-500">*</span> : null}</span>
      <div className="relative">
        <FileText size={17} className="pointer-events-none absolute left-4 top-4 text-slate-400" />
        <textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={5}
          className="w-full resize-none rounded-[16px] border bg-white pb-8 pl-12 pr-4 pt-4 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]"
          style={{ borderColor: "#dfe5ee" }}
        />
        <span className="pointer-events-none absolute bottom-3 right-4 text-xs font-medium text-slate-400">{String(value || "").length}/{maxLength}</span>
      </div>
    </label>
  );
}

function SelectInput({ label, value, onChange, options, disabled = false, required = false }) {
  return (
    <label className="block space-y-2.5">
      <span className="text-[13px] font-semibold text-slate-800">{label} {required ? <span className="text-red-500">*</span> : null}</span>
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          disabled={disabled}
          className="h-[54px] w-full appearance-none rounded-[16px] border bg-white pl-4 pr-10 text-sm text-slate-700 outline-none transition disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]"
          style={{ borderColor: "#dfe5ee" }}
        >
          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <ChevronDown size={17} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
      </div>
    </label>
  );
}

function toIsoFromLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString();
}

function toLocalInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function normalizeItems(data) {
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
}

function normalizeOne(data) {
  return data?.item || data?.data?.item || data?.data || data || null;
}

function optionValue(item, keys) {
  if (typeof item === "string") return item;
  for (const key of keys) if (item?.[key]) return item[key];
  return "";
}

function optionLabel(item, keys) {
  if (typeof item === "string") return item;
  for (const key of keys) if (item?.[key]) return item[key];
  return optionValue(item, keys);
}

function audienceLabel(value) {
  return audienceOptions.find((item) => item.value === value)?.label || "Users";
}

function StatusBadge({ active }) {
  return <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{active ? "Active" : "Inactive"}</span>;
}

function AudienceBadge({ audience }) {
  const item = audienceOptions.find((x) => x.value === audience) || audienceOptions[0];
  const Icon = item.icon;
  return <span className="inline-flex items-center gap-2 rounded-full bg-[#fff4f6] px-3 py-1.5 text-xs font-semibold text-[#d86a78]"><Icon size={13} />{item.label}</span>;
}

function targetText(item, warehouseMap = {}) {
  if (item.filter_type === "state") {
    return `State: ${item.target_state || "-"}`;
  }

  if (item.filter_type === "city") {
    return `City: ${item.target_city || "-"}, ${item.target_state || "-"}`;
  }

  if (item.filter_type === "warehouse") {
    const id = item.target_warehouse_id || item.warehouseId || item.warehouse_id || "";
    const name =
      item.target_warehouse_name ||
      item.warehouse_name ||
      item.name ||
      warehouseMap[id] ||
      id ||
      "-";

    return `Warehouse: ${name}`;
  }

  return "All audience";
}

function statNumber(value) {
  return Number(value || 0);
}

function BroadcastFormModal({
  open,
  mode = "create",
  item,
  defaultAudience,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(initialForm);
  const [sendEmail, setSendEmail] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectedAudience =
    audienceOptions.find((x) => x.value === form.audience) || audienceOptions[0];

  const SelectedIcon = selectedAudience.icon;
  const canShowState = ["state", "city", "warehouse"].includes(form.filterType);
  const canShowCity = ["city", "warehouse"].includes(form.filterType);
  const canShowWarehouse = form.filterType === "warehouse";

  const patchForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const loadStates = useCallback(async (audience) => {
    setLoadingOptions(true);
    try {
      setStates(normalizeItems(await broadcastsService.getStates(audience)));
    } catch {
      setStates([]);
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  const loadCities = useCallback(async (audience, state) => {
    if (!state) {
      setCities([]);
      return;
    }

    setLoadingOptions(true);
    try {
      setCities(normalizeItems(await broadcastsService.getCities(audience, { state })));
    } catch {
      setCities([]);
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  const loadWarehouses = useCallback(async (audience, state, city) => {
    setLoadingOptions(true);
    try {
      setWarehouses(
        normalizeItems(await broadcastsService.getWarehouses(audience, { state, city }))
      );
    } catch {
      setWarehouses([]);
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    const nextAudience = item?.audience || defaultAudience || "users";

    if (mode === "edit" && item) {
      const nextSendEmail = Boolean(item.send_email ?? item.sendEmail ?? false);

      setForm({
        audience: nextAudience,
        title: item.title || "",
        body: item.body || "",
        actionUrl: item.action_url || "",
        filterType: item.filter_type || "all",
        state: item.target_state || "",
        city: item.target_city || "",
        warehouseId: item.target_warehouse_id || "",
        startAtLocal: toLocalInputValue(item.start_at),
        endAtLocal: toLocalInputValue(item.end_at),
        image: null,
        sendEmail: nextSendEmail,
      });

      setSendEmail(nextSendEmail);
      setPreviewUrl(buildBroadcastImageUrl(item.image_url));
      loadStates(nextAudience);

      if (item.target_state) {
        loadCities(nextAudience, item.target_state);
      }

      if (item.filter_type === "warehouse") {
        loadWarehouses(nextAudience, item.target_state, item.target_city);
      }
    } else {
      setForm({ ...initialForm, audience: nextAudience });
      setSendEmail(false);
      setPreviewUrl("");
      setCities([]);
      setWarehouses([]);
      loadStates(nextAudience);
    }

    setError("");
  }, [open, mode, item, defaultAudience, loadStates, loadCities, loadWarehouses]);

  function validate() {
    if (!form.title.trim()) return "Title is required";
    if (!form.body.trim()) return "Message body is required";
    if (!form.startAtLocal) return "Start date/time is required";
    if (!form.endAtLocal) return "End date/time is required";

    const start = new Date(form.startAtLocal);
    const end = new Date(form.endAtLocal);

    if (Number.isNaN(start.getTime())) return "Start date/time is invalid";
    if (Number.isNaN(end.getTime())) return "End date/time is invalid";
    if (start >= end) return "Start date/time must be before end date/time";

    if (form.filterType === "state" && !form.state) return "State is required";
    if (form.filterType === "city" && (!form.state || !form.city)) {
      return "State and city are required";
    }
    if (form.filterType === "warehouse" && !form.warehouseId) {
      return "Warehouse is required";
    }

    return "";
  }

  async function handleSave() {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        audience: form.audience,
        title: form.title,
        body: form.body,
        actionUrl: form.actionUrl,
        filterType: form.filterType,
        state: form.filterType === "all" ? "" : form.state,
        city: ["city", "warehouse"].includes(form.filterType) ? form.city : "",
        warehouseId: form.filterType === "warehouse" ? form.warehouseId : "",
        startAt: toIsoFromLocal(form.startAtLocal),
        endAt: toIsoFromLocal(form.endAtLocal),
        image: form.image,
        sendEmail,
      };

      console.log("BROADCAST SAVE PAYLOAD:", payload);

      if (mode === "edit" && item?.id) {
        await broadcastsService.update(item.id, payload);
      } else {
        await broadcastsService.create(payload);
      }

      await onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || "Unable to save broadcast");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Edit Broadcast" : "Create New Broadcast"}
      subtitle="Create popup/banner broadcasts for users, delivery partners, or warehouses."
      width="max-w-[1280px]"
    >
      <div className="bg-[#fcfcfd]">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_360px]">
          <div
            className="min-w-0 border-r px-6 py-6 sm:px-8"
            style={{ borderColor: "#e8edf4" }}
          >
            {error ? (
              <div className="mb-5 rounded-[16px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                {error}
              </div>
            ) : null}

            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                {audienceOptions.map((aud) => {
                  const Icon = aud.icon;
                  const active = form.audience === aud.value;

                  return (
                    <button
                      key={aud.value}
                      type="button"
                      onClick={() => {
                        patchForm({
                          audience: aud.value,
                          filterType: "all",
                          state: "",
                          city: "",
                          warehouseId: "",
                        });
                        setCities([]);
                        setWarehouses([]);
                        loadStates(aud.value);
                      }}
                      className={`flex min-h-[82px] items-center gap-3 rounded-[20px] border p-4 text-left transition ${
                        active
                          ? "bg-[#fff6f7] text-[#d86a78]"
                          : "bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                      style={{ borderColor: active ? PINK : "#dfe5ee" }}
                    >
                      <span className="grid h-11 w-11 place-items-center rounded-[16px] bg-white shadow-sm">
                        <Icon size={20} />
                      </span>
                      <span className="text-sm font-bold">{aud.label}</span>
                    </button>
                  );
                })}
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <TextInput
                  label="Title"
                  required
                  value={form.title}
                  onChange={(e) => patchForm({ title: e.target.value })}
                  placeholder="Enter broadcast title"
                  icon={Type}
                  maxLength={TITLE_LIMIT}
                />

                <TextInput
                  label="Action URL"
                  value={form.actionUrl}
                  onChange={(e) => patchForm({ actionUrl: e.target.value })}
                  placeholder="/offers or https://example.com"
                  icon={Link2}
                />
              </div>

              <TextArea
                label="Message body"
                required
                value={form.body}
                onChange={(e) => patchForm({ body: e.target.value })}
                placeholder="Write the message that will be shown in the popup/banner"
              />

              <div
                className="flex items-center justify-between gap-4 rounded-[18px] border bg-white px-4 py-4"
                style={{ borderColor: "#dfe5ee" }}
              >
                <div className="flex items-start gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-[14px] bg-[#fff1f3] text-[#d86a78]">
                    <Send size={18} />
                  </div>

                  <div>
                    <div className="text-[14px] font-bold text-slate-900">
                      Send Email also
                    </div>
                    <div className="mt-1 text-xs leading-5 text-slate-500">
                      ON karne par same broadcast title, message, image aur action
                      URL selected audience ko email me bhi jayega.
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setSendEmail((prev) => !prev)}
                  className={`relative h-7 w-14 rounded-full transition ${
                    sendEmail ? "bg-[#d86a78]" : "bg-slate-300"
                  }`}
                  aria-pressed={sendEmail}
                  title={sendEmail ? "Email ON" : "Email OFF"}
                >
                  <span
                    className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${
                      sendEmail ? "left-8" : "left-1"
                    }`}
                  />
                </button>
              </div>

              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                <SelectInput
                  label="Filter type"
                  required
                  value={form.filterType}
                  onChange={(e) => {
                    const next = e.target.value;
                    patchForm({
                      filterType: next,
                      state: "",
                      city: "",
                      warehouseId: "",
                    });
                    setCities([]);
                    setWarehouses([]);
                    if (next !== "all") loadStates(form.audience);
                    if (next === "warehouse") {
                      loadWarehouses(form.audience, "", "");
                    }
                  }}
                  options={filterOptions}
                />

                {canShowState ? (
                  <SelectInput
                    label="State"
                    value={form.state}
                    disabled={loadingOptions}
                    onChange={(e) => {
                      const state = e.target.value;
                      patchForm({ state, city: "", warehouseId: "" });
                      setWarehouses([]);
                      loadCities(form.audience, state);
                      if (form.filterType === "warehouse") {
                        loadWarehouses(form.audience, state, "");
                      }
                    }}
                    options={[
                      {
                        value: "",
                        label: loadingOptions ? "Loading states..." : "Select state",
                      },
                      ...states.map((x) => ({
                        value: optionValue(x, ["state", "name"]),
                        label: optionLabel(x, ["state", "name"]),
                      })),
                    ]}
                  />
                ) : null}

                {canShowCity ? (
                  <SelectInput
                    label="City"
                    value={form.city}
                    disabled={!form.state || loadingOptions}
                    onChange={(e) => {
                      const city = e.target.value;
                      patchForm({ city, warehouseId: "" });
                      if (form.filterType === "warehouse") {
                        loadWarehouses(form.audience, form.state, city);
                      }
                    }}
                    options={[
                      {
                        value: "",
                        label: loadingOptions ? "Loading cities..." : "Select city",
                      },
                      ...cities.map((x) => ({
                        value: optionValue(x, ["city", "name"]),
                        label: optionLabel(x, ["city", "name"]),
                      })),
                    ]}
                  />
                ) : null}

                {canShowWarehouse ? (
                  <SelectInput
                    label="Warehouse"
                    value={form.warehouseId}
                    disabled={loadingOptions}
                    onChange={(e) => patchForm({ warehouseId: e.target.value })}
                    options={[
                      {
                        value: "",
                        label: loadingOptions
                          ? "Loading warehouses..."
                          : "Select warehouse",
                      },
                      ...warehouses.map((x) => ({
                        value: optionValue(x, ["id", "warehouseId", "warehouse_id"]),
                        label: `${optionLabel(x, [
                          "name",
                          "warehouse_name",
                          "code",
                          "id",
                        ])}${x?.city ? ` · ${x.city}` : ""}${
                          x?.state ? `, ${x.state}` : ""
                        }`,
                      })),
                    ]}
                  />
                ) : null}
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <TextInput
                  label="Start At"
                  required
                  type="datetime-local"
                  value={form.startAtLocal}
                  onChange={(e) => patchForm({ startAtLocal: e.target.value })}
                  icon={CalendarDays}
                />

                <TextInput
                  label="End At"
                  required
                  type="datetime-local"
                  value={form.endAtLocal}
                  onChange={(e) => patchForm({ endAtLocal: e.target.value })}
                  icon={CalendarDays}
                />
              </div>
            </div>
          </div>

          <div className="min-w-0 px-6 py-6">
            <div className="space-y-4">
              <div
                className="rounded-[22px] border bg-white p-5 shadow-sm"
                style={{ borderColor: "#e5eaf2" }}
              >
                <div className="mb-4 flex items-center gap-3 text-[16px] font-semibold text-slate-900">
                  <ImagePlus size={18} />
                  Broadcast Image
                </div>

                <label
                  className="flex min-h-[170px] cursor-pointer flex-col items-center justify-center rounded-[18px] border border-dashed px-5 py-6 text-center transition hover:bg-[#fffafb]"
                  style={{
                    borderColor: "#f3c8d0",
                    backgroundColor: "#fffdfd",
                  }}
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Broadcast preview"
                      className="h-[170px] w-full rounded-[14px] object-cover"
                    />
                  ) : (
                    <>
                      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-[#fff1f3] text-[#d86a78]">
                        <ImagePlus size={22} />
                      </div>
                      <div className="text-[15px] font-semibold text-slate-900">
                        Choose image
                      </div>
                      <div className="mt-2 text-sm text-slate-500">
                        PNG, JPG, JPEG, WEBP
                      </div>
                    </>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      patchForm({ image: file });

                      if (file) {
                        setPreviewUrl(URL.createObjectURL(file));
                      }
                    }}
                  />
                </label>
              </div>

              <div
                className="rounded-[22px] border bg-white p-5 shadow-sm"
                style={{ borderColor: "#e5eaf2" }}
              >
                <div className="mb-4 flex items-center gap-3 text-[16px] font-semibold text-slate-900">
                  <Eye size={18} />
                  Quick Preview
                </div>

                <div
                  className="rounded-[20px] border px-6 py-8 text-center"
                  style={{
                    borderColor: "#e9edf4",
                    backgroundColor: "#f7f8fc",
                  }}
                >
                  <div className="mx-auto mb-5 flex h-[82px] w-[82px] items-center justify-center rounded-[18px] bg-[#fbecef] text-[#d86a78]">
                    <SelectedIcon size={32} />
                  </div>

                  <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold text-[#d86a78]">
                    <SelectedIcon size={13} />
                    {selectedAudience.label}
                  </div>

                  <div className="mt-4 break-words text-[18px] font-bold text-slate-900">
                    {form.title || "Broadcast title"}
                  </div>

                  <div className="mt-3 break-words whitespace-pre-wrap text-sm leading-6 text-slate-500">
                    {form.body || "Broadcast message will appear here."}
                  </div>

                  <div className="mt-5 rounded-full bg-slate-200 px-4 py-1.5 text-sm font-semibold text-slate-600">
                    {filterOptions.find((x) => x.value === form.filterType)?.label ||
                      "All"}
                  </div>

                  <div
                    className={`mx-auto mt-3 inline-flex rounded-full px-4 py-1.5 text-sm font-semibold ${
                      sendEmail
                        ? "bg-[#fff1f3] text-[#d86a78]"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    Email: {sendEmail ? "ON" : "OFF"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="flex flex-col-reverse gap-3 border-t bg-white px-6 py-5 sm:flex-row sm:items-center sm:justify-between"
          style={{ borderColor: "#e8edf4" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-[50px] items-center justify-center rounded-[14px] border bg-white px-6 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            style={{ borderColor: "#dfe5ee" }}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex h-[50px] items-center justify-center gap-2 rounded-[14px] px-6 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70"
            style={{ backgroundColor: PINK }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = PINK_HOVER;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = PINK;
            }}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {saving
              ? "Saving..."
              : mode === "edit"
                ? "Update Broadcast"
                : "Create Broadcast"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function BroadcastViewModal({ open, item, onClose, warehouseMap = {} }) {
  if (!open || !item) return null;
  return (
    <ModalShell open={open} onClose={onClose} title="Broadcast Details" subtitle="Complete broadcast information">
      <div className="grid gap-6 px-5 py-5 sm:px-6 lg:grid-cols-[1fr_1fr]">
        <div className="overflow-hidden rounded-[22px] border bg-slate-50" style={{ borderColor: BORDER }}>
          {item.image_url ? <img src={buildBroadcastImageUrl(item.image_url)} alt={item.title} className="h-full min-h-[280px] w-full object-cover" /> : <div className="flex min-h-[280px] items-center justify-center text-slate-400">No image</div>}
        </div>
        <div className="space-y-4">
          <div><AudienceBadge audience={item.audience || "users"} /><div className="mt-3 text-[24px] font-bold text-slate-900">{item.title || "-"}</div><p className="mt-2 text-sm leading-6 text-slate-500">{item.body || "No message body"}</p></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}><div className="text-xs uppercase text-slate-400">Status</div><div className="mt-2"><StatusBadge active={!!item.is_active} /></div></div>
            <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}><div className="text-xs uppercase text-slate-400">Filter</div><div className="mt-2 text-sm font-semibold text-slate-700">{item.filter_type || "all"}</div></div>
            <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}><div className="text-xs uppercase text-slate-400">Start</div><div className="mt-2 text-sm font-semibold text-slate-700">{formatDate(item.start_at)}</div></div>
            <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}><div className="text-xs uppercase text-slate-400">End</div><div className="mt-2 text-sm font-semibold text-slate-700">{formatDate(item.end_at)}</div></div>
         </div>

<div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}>
  <div className="mb-2 text-xs uppercase text-slate-400">Audience Target</div>
  <div className="text-sm text-slate-700">
  {targetText(item, warehouseMap)}
</div>
</div>
          <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}><div className="mb-2 text-xs uppercase text-slate-400">Action URL</div><div className="break-all text-sm text-slate-700">{item.action_url || "-"}</div></div>
          <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}>
            <div className="mb-2 text-xs uppercase text-slate-400">Email</div>

            <div
              className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                Boolean(item.send_email ?? item.sendEmail)
                  ? "bg-[#fff1f3] text-[#d86a78]"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              {Boolean(item.send_email ?? item.sendEmail) ? "Email ON" : "Email OFF"}
            </div>

            {Boolean(item.send_email ?? item.sendEmail) ? (
              <div className="mt-3 text-sm leading-6 text-slate-600">
                Sent: <b>{statNumber(item.email_sent_count ?? item.emailSentCount)}</b>
                <br />
                Failed: <b>{statNumber(item.email_failed_count ?? item.emailFailedCount)}</b>
                <br />
                Sent At: <b>{formatDate(item.email_sent_at ?? item.emailSentAt)}</b>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}


function ThemePopup({ open, variant = "success", title, message, onClose }) {
  if (!open) return null;

  const isError = variant === "error";
  const Icon = isError ? AlertCircle : CheckCircle2;

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/45 px-4 py-6">
      <div
        className="w-full max-w-[430px] overflow-hidden rounded-[28px] border bg-white shadow-2xl"
        style={{ borderColor: BORDER }}
      >
        <div className="px-6 py-7 text-center">
          <div
            className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
              isError
                ? "bg-red-50 text-red-500"
                : "bg-[#fff1f3] text-[#d86a78]"
            }`}
          >
            <Icon size={30} />
          </div>

          <h3 className="text-[21px] font-bold text-slate-900">{title}</h3>

          {message ? (
            <p className="mt-2 text-sm leading-6 text-slate-500">{message}</p>
          ) : null}

          <button
            type="button"
            onClick={onClose}
            className="mt-6 inline-flex h-[46px] min-w-[140px] items-center justify-center rounded-[14px] px-5 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(216,106,120,0.20)] transition hover:-translate-y-0.5"
            style={{ backgroundColor: isError ? "#ef4444" : PINK }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmPopup({
  open,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading = false,
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/45 px-4 py-6">
      <div
        className="w-full max-w-[470px] overflow-hidden rounded-[28px] border bg-white shadow-2xl"
        style={{ borderColor: BORDER }}
      >
        <div className="px-6 py-7 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#fff1f3] text-[#d86a78]">
            <Trash2 size={28} />
          </div>

          <h3 className="text-[21px] font-bold text-slate-900">{title}</h3>

          {message ? (
            <p className="mt-2 text-sm leading-6 text-slate-500">{message}</p>
          ) : null}

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="inline-flex h-[46px] items-center justify-center rounded-[14px] border bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ borderColor: "#dfe5ee" }}
            >
              {cancelText}
            </button>

            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="inline-flex h-[46px] items-center justify-center gap-2 rounded-[14px] px-5 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(216,106,120,0.20)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
              style={{ backgroundColor: PINK }}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              {loading ? "Deleting..." : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BroadcastMessagesPage() {
  const [items, setItems] = useState([]);
  const [audience, setAudience] = useState("users");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [q, setQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [warehouseMap, setWarehouseMap] = useState({});
  const [themePopup, setThemePopup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  function showPopup(variant, title, message) {
    setThemePopup({ variant, title, message });
  }

  const total = items.length;
  const totalPages = 1;

  const activeCount = useMemo(() => items.filter((item) => !!item.is_active).length, [items]);
  const filteredCount = useMemo(() => items.filter((item) => item.filter_type && item.filter_type !== "all").length, [items]);

  const visibleItems = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((item) => {
      if (statusFilter === "true" && !item.is_active) return false;
      if (statusFilter === "false" && item.is_active) return false;
      if (!query) return true;
      return [item.title, item.body, item.action_url, item.target_state, item.target_city, item.target_warehouse_id].some((value) => String(value || "").toLowerCase().includes(query));
    });
  }, [items, q, statusFilter]);

  const loadWarehouseMap = useCallback(async () => {
  try {
    const res = await broadcastsService.getWarehouses(audience, {});
    const list = normalizeItems(res);

    const map = {};
    list.forEach((item) => {
      const id = optionValue(item, ["id", "warehouseId", "warehouse_id", "value"]);
      const name = optionLabel(item, ["name", "warehouse_name", "label", "code", "id"]);

      if (id) map[id] = name || id;
    });

    setWarehouseMap(map);
  } catch {
    setWarehouseMap({});
  }
}, [audience]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await broadcastsService.list({ audience, page, limit });
      const nextItems = normalizeItems(res).map((item) => ({ ...item, audience: item.audience || audience }));
      setItems(nextItems);
    } catch (err) {
      setError(err?.message || "Unable to load broadcasts");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [audience, page, limit]);

  useEffect(() => {
  loadData();
  loadWarehouseMap();
}, [loadData, loadWarehouseMap]);

  async function handleView(item) {
    try {
      const res = await broadcastsService.getById(item.id, item.audience || audience);
      setViewItem({ ...normalizeOne(res), audience: item.audience || audience });
    } catch {
      setViewItem(item);
    }
  }

  async function handleToggleStatus(item) {
    if (!item?.id) return;

    setBusyId(item.id);

    try {
      await broadcastsService.setActive(
        item.id,
        !item.is_active,
        item.audience || audience
      );

      await loadData();

      showPopup(
        "success",
        item.is_active ? "Broadcast Deactivated" : "Broadcast Activated",
        item.is_active
          ? "This broadcast is now inactive."
          : "This broadcast is now active."
      );
    } catch (err) {
      showPopup(
        "error",
        "Status Update Failed",
        err?.message || "Unable to update status"
      );
    } finally {
      setBusyId("");
    }
  }

  function handleDelete(item) {
    if (!item?.id) return;
    setDeleteTarget(item);
  }

  async function confirmDeleteBroadcast() {
    if (!deleteTarget?.id) return;

    setBusyId(deleteTarget.id);

    try {
      await broadcastsService.remove(
        deleteTarget.id,
        deleteTarget.audience || audience
      );

      setDeleteTarget(null);
      await loadData();

      showPopup(
        "success",
        "Broadcast Deleted",
        "Broadcast deleted successfully."
      );
    } catch (err) {
      showPopup(
        "error",
        "Delete Failed",
        err?.message || "Unable to delete broadcast"
      );
    } finally {
      setBusyId("");
    }
  }

  async function handleCreateSaved() {
    await loadData();
    showPopup(
      "success",
      "Broadcast Created",
      "New broadcast created successfully."
    );
  }

  async function handleEditSaved() {
    await loadData();
    showPopup(
      "success",
      "Broadcast Updated",
      "Broadcast updated successfully."
    );
  }

  return (
    <div className="space-y-3">
      <Card className="overflow-hidden border-0 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
        <div className="px-4 py-3 sm:px-5 lg:px-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#fff4f6] px-3 py-1.5 text-xs font-semibold text-[#d86a78]"><Megaphone size={16} />Broadcast Module</div>
              <h1 className="mt-2 text-[22px] font-bold leading-tight text-[#dc667b] sm:text-[26px]">Broadcast Messages</h1>
              <p className="mt-1 max-w-[760px] text-[13px] leading-5 text-slate-500">Create, edit, view, activate, and delete popup/banner broadcasts for users, delivery partners, and warehouses.</p>
            </div>
            <button type="button" onClick={() => setCreateOpen(true)} className="inline-flex h-[42px] items-center justify-center gap-2 rounded-[14px] px-4 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(216,106,120,0.20)] transition hover:-translate-y-0.5" style={{ backgroundColor: PINK }} onMouseOver={(e) => (e.currentTarget.style.backgroundColor = PINK_HOVER)} onMouseOut={(e) => (e.currentTarget.style.backgroundColor = PINK)}><Plus size={18} />New Broadcast</button>
          </div>
        </div>
      </Card>

      {error ? <div className="flex items-center gap-2 rounded-[18px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"><AlertCircle size={17} />{error}</div> : null}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4 [&>div]:min-h-0 [&>div]:p-3">
        <div className="rounded-[18px] border bg-white p-4 shadow-[0_8px_28px_rgba(15,23,42,0.04)]" style={{ borderColor: "#f7d7dc" }}><div className="flex items-center gap-3"><div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#fff1f3] text-[#d86a78]"><LayoutGrid size={20} /></div><div className="text-[12px] font-semibold uppercase tracking-[0.04em] text-slate-500">Total Broadcasts</div></div><div className="mt-2 text-[24px] font-bold leading-none text-slate-900">{total}</div><div className="mt-1 text-[11px] font-medium text-slate-500">Selected audience list</div></div>
        <div className="rounded-[18px] border bg-white shadow-[0_8px_28px_rgba(15,23,42,0.04)]" style={{ borderColor: "#d8f0e3" }}><div className="flex items-center gap-3"><div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#edf9f1] text-emerald-600"><Check size={20} /></div><div className="text-[12px] font-semibold uppercase tracking-[0.04em] text-slate-500">Active</div></div><div className="mt-2 text-[24px] font-bold leading-none text-slate-900">{activeCount}</div><div className="mt-1 text-[11px] font-medium text-slate-500">Active on current list</div></div>
        <div className="rounded-[18px] border bg-white shadow-[0_8px_28px_rgba(15,23,42,0.04)]" style={{ borderColor: "#dfe7fb" }}><div className="flex items-center gap-3"><div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#eef3ff] text-[#4f7cff]"><MapPin size={20} /></div><div className="text-[12px] font-semibold uppercase tracking-[0.04em] text-slate-500">Filtered</div></div><div className="mt-2 text-[24px] font-bold leading-none text-slate-900">{filteredCount}</div><div className="mt-1 text-[11px] font-medium text-slate-500">State/city/warehouse targeting</div></div>
        <div className="rounded-[18px] border bg-white shadow-[0_8px_28px_rgba(15,23,42,0.04)]" style={{ borderColor: "#fde7c8" }}><div className="flex items-center gap-3"><div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#fff5e8] text-[#f59e0b]"><Globe2 size={20} /></div><div className="text-[12px] font-semibold uppercase tracking-[0.04em] text-slate-500">Audience</div></div><div className="mt-2 truncate text-[20px] font-bold leading-none text-slate-900">{audienceLabel(audience)}</div><div className="mt-1 text-[11px] font-medium text-slate-500">Currently selected</div></div>
      </div>

      <Card className="overflow-hidden border-0 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
        <div className="flex flex-col gap-3 border-b bg-[#fcfcfd] px-4 py-3 sm:px-5" style={{ borderColor: "#eef2f7" }}>
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
              <BroadcastFilterDropdown
                value={audience}
                onChange={(value) => {
                  setAudience(value);
                  setPage(1);
                }}
                minWidth="min-w-[180px]"
                options={audienceOptions.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
              />

              <div className="relative w-full xl:max-w-[520px]"><Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} /><input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setQ(searchInput.trim()); }} placeholder="Search title, message, URL, state, city..." className="h-[44px] w-full rounded-[14px] border bg-white py-3 pl-12 pr-4 text-sm text-slate-700 outline-none transition focus:ring-2 focus:ring-[#f7d4da]" style={{ borderColor: "#e7eaf0" }} /></div>

              <BroadcastFilterDropdown
                value={statusFilter}
                onChange={setStatusFilter}
                minWidth="min-w-[150px]"
                options={[
                  { value: "", label: "All Status" },
                  { value: "true", label: "Active" },
                  { value: "false", label: "Inactive" },
                ]}
              />
            </div>
            <div className="flex flex-wrap gap-3"><button type="button" onClick={() => setQ(searchInput.trim())} className="inline-flex h-[44px] items-center justify-center gap-2 rounded-[14px] border bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50" style={{ borderColor: "#e7eaf0" }}><Search size={16} />Search</button><button type="button" onClick={() => { setSearchInput(""); setQ(""); setStatusFilter(""); }} className="inline-flex h-[44px] items-center justify-center gap-2 rounded-[14px] border bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50" style={{ borderColor: "#e7eaf0" }}><X size={16} />Reset</button></div>
          </div>
        </div>

        <div className="table-scrollbar overflow-x-auto xl:overflow-hidden">
          <table className="min-w-[1050px] text-left xl:w-full xl:min-w-0 xl:table-fixed">
            <colgroup>
              <col style={{ width: "23%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "14%" }} />
            </colgroup>
            <thead><tr className="border-b bg-white text-slate-500" style={{ borderColor: "#eef2f7" }}><th className="px-4 py-3 text-[13px] font-semibold">Broadcast</th><th className="px-4 py-3 text-[13px] font-semibold">Audience</th><th className="px-4 py-3 text-[13px] font-semibold">Target</th><th className="px-4 py-3 text-[13px] font-semibold">Schedule</th><th className="px-4 py-3 text-[13px] font-semibold">Views</th>
<th className="px-4 py-3 text-[13px] font-semibold">Email</th>
<th className="px-4 py-3 text-[13px] font-semibold">Status</th>
<th className="px-4 py-3 text-center text-[13px] font-semibold">Actions</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-500">Loading broadcasts...</td></tr> : null}
              {!loading && visibleItems.length === 0 ? <tr><td colSpan={8} className="px-4 py-10 text-center"><div className="mx-auto flex max-w-[360px] flex-col items-center"><div className="rounded-full bg-[#fff4f6] p-3 text-[#d86a78]"><Megaphone size={22} /></div><div className="mt-3 text-base font-bold text-slate-800">No broadcasts found</div><div className="mt-1 text-xs text-slate-500">Create your first broadcast to start using this module.</div></div></td></tr> : null}
              {!loading && visibleItems.map((item) => {
                const disabled = busyId === item.id;
                const img = buildBroadcastImageUrl(item.image_url || item.imageUrl);
                return (
                  <tr key={item.id} className="border-b align-middle transition hover:bg-[#fcfcfd]" style={{ borderColor: "#eef2f7" }}>
                    <td className="px-3 py-2.5"><div className="flex min-w-0 items-center gap-2.5"><div className="h-11 w-11 shrink-0 overflow-hidden rounded-[12px] border bg-slate-50 shadow-sm" style={{ borderColor: "#edf0f5" }}>{img ? <img src={img} alt={item.title} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-slate-400"><Megaphone size={18} /></div>}</div><div className="min-w-0"><div className="truncate text-[14px] font-semibold text-slate-800">{item.title || "-"}</div><div className="mt-0.5 line-clamp-2 text-[13px] leading-5 text-slate-500">{item.body || "No message body"}</div>{item.action_url ? <div className="mt-1 truncate text-[11px] font-medium text-[#d86a78]">{item.action_url}</div> : null}</div></div></td>
                    <td className="px-3 py-2.5"><AudienceBadge audience={item.audience || audience} /></td>
                    <td className="px-3 py-2.5"><div className="line-clamp-2 text-[13px] leading-5 text-slate-600">{targetText(item, warehouseMap)}</div></td>
                    <td className="px-3 py-2.5"><div><div className="text-[13px] font-semibold leading-5 text-slate-700">{formatDate(item.start_at)}</div><div className="mt-0.5 text-[11px] leading-4 text-slate-500">{formatDate(item.end_at)}</div></div></td>
                   <td className="px-3 py-2.5">
  <div className="text-[11px] leading-4 text-slate-500">
    <div>
      Viewed:{" "}
      <span className="font-bold text-slate-800">
        {statNumber(item.unique_viewed_count)}
      </span>
      {" / "}
      <span className="font-bold text-slate-800">
        {statNumber(item.total_recipients)}
      </span>
    </div>

    <div>
      Pending:{" "}
      <span className="font-bold text-slate-800">
        {statNumber(item.pending_count)}
      </span>
    </div>

    <div>
      Total views:{" "}
      <span className="font-bold text-slate-800">
        {statNumber(item.total_views)}
      </span>
    </div>
  </div>
</td>

<td className="px-3 py-2.5">
  <div className="text-[11px] text-slate-500">
    <div
      className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
        Boolean(item.send_email ?? item.sendEmail)
          ? "bg-[#fff1f3] text-[#d86a78]"
          : "bg-slate-100 text-slate-500"
      }`}
    >
      {Boolean(item.send_email ?? item.sendEmail) ? "Email ON" : "Email OFF"}
    </div>

    {Boolean(item.send_email ?? item.sendEmail) ? (
      <div className="mt-2 leading-5">
        Sent: {" "}
        <span className="font-bold text-slate-800">
          {statNumber(item.email_sent_count ?? item.emailSentCount)}
        </span>
        <br />
        Failed: {" "}
        <span className="font-bold text-slate-800">
          {statNumber(item.email_failed_count ?? item.emailFailedCount)}
        </span>
      </div>
    ) : null}
  </div>
</td>

<td className="px-3 py-2.5"><StatusBadge active={!!item.is_active} /></td>
                    <td className="px-2 py-2.5"><div className="flex items-center justify-center gap-1"><button type="button" onClick={() => handleView(item)} className="rounded-[9px] border p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-[#d86a78]" style={{ borderColor: "#e7eaf0" }} title="View"><Eye size={15} /></button><button type="button" onClick={() => setEditItem(item)} className="rounded-[9px] border p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-[#d86a78]" style={{ borderColor: "#e7eaf0" }} title="Edit"><Pencil size={15} /></button><button type="button" disabled={disabled} onClick={() => handleToggleStatus(item)} className="rounded-[9px] border p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-[#d86a78] disabled:cursor-not-allowed disabled:opacity-60" style={{ borderColor: "#e7eaf0" }} title={item.is_active ? "Deactivate" : "Activate"}><Power size={15} /></button><button type="button" disabled={disabled} onClick={() => handleDelete(item)} className="rounded-[9px] border p-1.5 text-red-500 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60" style={{ borderColor: "#f1d1d5" }} title="Delete"><Trash2 size={15} /></button></div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5" style={{ borderColor: "#eef2f7" }}><div className="text-sm text-slate-500">Showing {visibleItems.length} of {items.length} broadcasts</div><div className="flex flex-wrap items-center gap-2"><button type="button" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))} className="flex h-9 w-9 items-center justify-center rounded-[12px] border bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50" style={{ borderColor: "#e7eaf0" }}><ChevronLeft size={18} /></button><button type="button" className="h-9 min-w-[36px] rounded-[12px] px-2 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(216,106,120,0.24)]" style={{ backgroundColor: PINK }}>{page}</button><button type="button" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} className="flex h-9 w-9 items-center justify-center rounded-[12px] border bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50" style={{ borderColor: "#e7eaf0" }}><ChevronRight size={18} /></button></div></div>
      </Card>

      <BroadcastFormModal
        open={createOpen}
        defaultAudience={audience}
        onClose={() => setCreateOpen(false)}
        onSaved={handleCreateSaved}
      />

      <BroadcastFormModal
        open={!!editItem}
        mode="edit"
        item={editItem}
        defaultAudience={audience}
        onClose={() => setEditItem(null)}
        onSaved={handleEditSaved}
      />

      <BroadcastViewModal
        open={!!viewItem}
        item={viewItem}
        warehouseMap={warehouseMap}
        onClose={() => setViewItem(null)}
      />

      <ConfirmPopup
        open={!!deleteTarget}
        title="Delete Broadcast?"
        message={`Are you sure you want to delete "${deleteTarget?.title || ""}"? This action cannot be undone.`}
        confirmText="Delete Broadcast"
        loading={busyId === deleteTarget?.id}
        onCancel={() => {
          if (busyId) return;
          setDeleteTarget(null);
        }}
        onConfirm={confirmDeleteBroadcast}
      />

      <ThemePopup
        open={!!themePopup}
        variant={themePopup?.variant}
        title={themePopup?.title}
        message={themePopup?.message}
        onClose={() => setThemePopup(null)}
      />
    </div>
  );
}
